import React, { useState, useEffect, useCallback } from 'react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { getFolderBasePath } from '@/config/app-config';
import type { GroupFolderMapping } from '@/types';
import { listObjects } from '@/api/client';

// モックモードかどうか
const isMockMode = import.meta.env.VITE_APP_MOCK_MODE === 'true';

interface FolderTreeProps {
  accessibleFolders: GroupFolderMapping[];
  selectedFolder: string | null;
  onFolderSelect: (folderPath: string, label: string) => void;
  subFolders?: string[];
  currentPrefix?: string;
  refreshTrigger?: number;
  /** 選択不可にするフォルダのプレフィックス一覧（移動元フォルダおよびその配下を除外するために使用） */
  excludedPrefixes?: string[];
}

/**
 * フォルダツリーコンポーネント
 * ユーザーのグループに基づいてアクセス可能なフォルダと、その配下の子フォルダを動的に表示
 */
const FolderTree: React.FC<FolderTreeProps> = ({
  accessibleFolders,
  selectedFolder,
  onFolderSelect,
  subFolders,
  currentPrefix,
  refreshTrigger,
  excludedPrefixes = [],
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [subFolderCache, setSubFolderCache] = useState<Record<string, string[]>>({});
  const [loadingFolders, setLoadingFolders] = useState<Record<string, boolean>>({});

  // フォルダクリック時の選択処理
  const handleItemClick = useCallback((e: React.MouseEvent, folderPath: string, label: string) => {
    e.stopPropagation();
    onFolderSelect(folderPath, label);
  }, [onFolderSelect]);

  // サブフォルダの動的フェッチ
  const fetchSubfolders = useCallback(async (prefix: string) => {
    if (subFolderCache[prefix] || loadingFolders[prefix]) {
      return;
    }

    setLoadingFolders(prev => ({ ...prev, [prefix]: true }));
    try {
      if (isMockMode) {
        // モックモード時はダミーの遅延とサブフォルダを生成
        await new Promise(resolve => setTimeout(resolve, 300));
        const mockSubfolders = [
          `${prefix}reports/`,
          `${prefix}documents/`,
          `${prefix}archives/`,
        ];
        setSubFolderCache(prev => ({
          ...prev,
          [prefix]: mockSubfolders,
        }));
      } else {
        const response = await listObjects(prefix);
        if (response.success && response.data) {
          const fetchedFolders = response.data.folders;
          setSubFolderCache(prev => ({
            ...prev,
            [prefix]: fetchedFolders,
          }));
        }
      }
    } catch (err) {
      console.error(`Failed to fetch subfolders for ${prefix}:`, err);
    } finally {
      setLoadingFolders(prev => ({ ...prev, [prefix]: false }));
    }
  }, [subFolderCache, loadingFolders]);

  // 展開状態が変更された際、新たに展開されたアイテムの配下をフェッチ
  const handleExpandedItemsChange = useCallback(async (_event: React.SyntheticEvent | null, itemIds: string[]) => {
    setExpandedItems(itemIds);
    const newlyExpanded = itemIds.find(id => !expandedItems.includes(id));
    if (newlyExpanded) {
      await fetchSubfolders(newlyExpanded);
    }
  }, [expandedItems, fetchSubfolders]);

  // 外部からの選択フォルダ変更に伴う、親階層の自動展開とデータ取得
  useEffect(() => {
    if (!selectedFolder) return;

    // パスから親パスの一覧を生成 (例: "docs/人事部/docs/採用/2026/" -> ["docs/人事部/docs/採用/", "docs/人事部/docs/"])
    const parts = selectedFolder.split('/').filter(Boolean);
    const parentPaths: string[] = [];

    // ルートフォルダのベースパスは通常 docs/<グループ名>/docs/ (配列長で3つ以上)
    if (parts.length > 3) {
      for (let i = 3; i < parts.length; i++) {
        const path = parts.slice(0, i).join('/') + '/';
        parentPaths.push(path);
      }
    }

    if (parentPaths.length > 0) {
      setExpandedItems(prev => {
        const next = [...prev];
        parentPaths.forEach(p => {
          if (!next.includes(p)) {
            next.push(p);
          }
        });
        return next;
      });

      // 未キャッシュの親フォルダの配下データをロード
      parentPaths.forEach(p => {
        if (!subFolderCache[p] && !loadingFolders[p]) {
          fetchSubfolders(p);
        }
      });
    }
  }, [selectedFolder, fetchSubfolders, subFolderCache, loadingFolders]);

  // 初回ロード時にアクセス可能なルートフォルダ直下のフォルダを事前に取得
  useEffect(() => {
    accessibleFolders.forEach(folder => {
      const basePath = getFolderBasePath(folder.value);
      if (!subFolderCache[basePath] && !loadingFolders[basePath]) {
        fetchSubfolders(basePath);
      }
    });
  }, [accessibleFolders, fetchSubfolders, subFolderCache, loadingFolders]);

  // 外部から currentPrefix 配下のフォルダリスト（subFolders）が更新された場合、キャッシュと同期する
  useEffect(() => {
    if (currentPrefix && subFolders) {
      setSubFolderCache(prev => {
        const existing = prev[currentPrefix];
        if (
          existing &&
          existing.length === subFolders.length &&
          existing.every((val, idx) => val === subFolders[idx])
        ) {
          return prev;
        }
        return {
          ...prev,
          [currentPrefix]: subFolders,
        };
      });
    }
  }, [currentPrefix, subFolders]);

  // refreshTriggerが更新された場合、展開されているすべてのフォルダパスのキャッシュを再フェッチして更新する
  const handleRefresh = useCallback(async () => {
    const foldersToFetch = new Set<string>();

    // ルートフォルダを追加
    accessibleFolders.forEach(folder => {
      foldersToFetch.add(getFolderBasePath(folder.value));
    });

    // 展開中のフォルダを追加
    expandedItems.forEach(item => {
      foldersToFetch.add(item);
    });

    // 各フォルダ配下を取得してキャッシュを更新
    for (const prefix of foldersToFetch) {
      try {
        if (!isMockMode) {
          const response = await listObjects(prefix);
          if (response.success && response.data) {
            const fetchedFolders = response.data.folders;
            setSubFolderCache(prev => {
              const existing = prev[prefix];
              if (
                existing &&
                existing.length === fetchedFolders.length &&
                existing.every((val, idx) => val === fetchedFolders[idx])
              ) {
                return prev;
              }
              return {
                ...prev,
                [prefix]: fetchedFolders,
              };
            });
          }
        }
      } catch (err) {
        console.error(`Failed to refresh subfolders for ${prefix}:`, err);
      }
    }
  }, [accessibleFolders, expandedItems]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      handleRefresh();
    }
  }, [refreshTrigger, handleRefresh]);


  // ツリーアイテムの再帰的レンダリング
  const renderTreeItem = (itemId: string, label: string) => {
    const isSelected = selectedFolder === itemId;
    const childFolders = subFolderCache[itemId] || [];
    const isLoading = loadingFolders[itemId];
    // 移動元フォルダ自身またはその配下は選択不可
    const isExcluded = excludedPrefixes.some(
      (prefix) => itemId === prefix || itemId.startsWith(prefix)
    );

    return (
      <TreeItem
        key={itemId}
        itemId={itemId}
        label={
          <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
              ...(isExcluded && { opacity: 0.4, cursor: 'not-allowed' }),
            }}
            onClick={(e) => {
              if (isExcluded) { e.stopPropagation(); return; }
              handleItemClick(e, itemId, label);
            }}
          >
            {isSelected ? (
              <FolderOpenIcon fontSize="small" color="primary" />
            ) : (
              <FolderIcon fontSize="small" color={isExcluded ? 'disabled' : 'action'} />
            )}
            <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'} color={isExcluded ? 'text.disabled' : 'inherit'}>
              {label}
            </Typography>
            {isLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
          </Box>
        }
      >
        {childFolders.map((childPath) => {
          const parts = childPath.split('/').filter(Boolean);
          const childLabel = parts.pop() || '';
          return renderTreeItem(childPath, childLabel);
        })}
      </TreeItem>
    );
  };

  if (accessibleFolders.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          アクセス可能なフォルダがありません
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 1, height: '100%', overflow: 'auto' }}>
      <Typography variant="subtitle2" sx={{ px: 1, py: 1, fontWeight: 'bold' }}>
        フォルダ
      </Typography>

      <SimpleTreeView
        expandedItems={expandedItems}
        onExpandedItemsChange={handleExpandedItemsChange}
        selectedItems={selectedFolder || undefined}
        sx={{
          '& .MuiTreeItem-content': {
            py: 0.5,
          },
        }}
      >
        {accessibleFolders.map((folder) => {
          const basePath = getFolderBasePath(folder.value);
          return renderTreeItem(basePath, folder.label);
        })}
      </SimpleTreeView>
    </Paper>
  );
};

export default FolderTree;
